// frontend/src/marketing/data/pricing.js
//
// Static plan matrix for the public pricing page (names, taglines, limits,
// features) and the FALLBACK prices.
//
// Since PRICING-ADMIN-PLAN P6 the live monthly prices are served from
// `GET /api/public/pricing` (DB-backed) and overlaid by PricingPage.jsx — these
// `monthly` values are only shown when that call is unavailable (offline / SSR).
// The canonical price lives in the admin Pricing & Plans page / `plan_prices`.
//
// MIRRORS docs/knowledge/qrflow-pricing.md — keep the two in sync. If the plan
// matrix or fallback prices change, update that doc (the AI knowledge base reads
// it) AND this file.

/** Annual billing discount applied to the monthly price (≈ 2 months free). */
export const ANNUAL_DISCOUNT = 0.2;

export const PLANS = [
  {
    id: "free",
    name: "Free",
    monthly: 0,
    tagline: "Try QRFlow or personal use.",
    cta: "Start free",
    highlight: false,
    limits: {
      codes: "5 active codes",
      scans: "500 scans / month",
      seats: "1 user",
      retention: "30-day analytics",
    },
    features: [
      "Unlimited dynamic redirects",
      "Folders & tags",
      "Basic scan analytics",
      "CSV import / export",
      "No credit card required",
    ],
  },
  {
    id: "starter",
    name: "Starter",
    monthly: 19,
    tagline: "For individuals who outgrew Free.",
    cta: "Start free trial",
    highlight: false,
    limits: {
      codes: "50 active codes",
      scans: "5,000 scans / month",
      seats: "3 team members",
      retention: "90-day analytics",
    },
    features: [
      "Everything in Free",
      "More codes, scans & seats",
      "Folders, tags & bulk actions",
      "Email support",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    monthly: 59,
    tagline: "For growing teams that need the full toolkit.",
    cta: "Start free trial",
    highlight: true,
    badge: "Most popular",
    limits: {
      codes: "500 active codes",
      scans: "50,000 scans / month",
      seats: "10 team members",
      retention: "365-day analytics",
    },
    features: [
      "Everything in Starter",
      "REST API with scoped keys",
      "Analytics export",
      "Location heatmaps",
      "A/B testing with code variants",
    ],
  },
  {
    id: "business",
    name: "Business",
    monthly: 149,
    tagline: "For organisations operating at scale.",
    cta: "Start free trial",
    highlight: false,
    limits: {
      codes: "5,000 active codes",
      scans: "500,000 scans / month",
      seats: "50 team members",
      retention: "730-day analytics",
    },
    features: [
      "Everything in Pro",
      "White-label branding",
      "Highest limits across the board",
      "Priority support",
    ],
  },
];

/** Full feature-comparison matrix. `true`/`false`/string per plan id. */
export const COMPARISON = [
  { label: "Price (monthly)", free: "€0", starter: "€19", pro: "€59", business: "€149" },
  { label: "Active QR codes", free: "5", starter: "50", pro: "500", business: "5,000" },
  { label: "Scans per month", free: "500", starter: "5,000", pro: "50,000", business: "500,000" },
  { label: "Team members (seats)", free: "1", starter: "3", pro: "10", business: "50" },
  { label: "Analytics retention", free: "30 days", starter: "90 days", pro: "365 days", business: "730 days" },
  { label: "Dynamic codes & redirects", free: true, starter: true, pro: true, business: true },
  { label: "Folders & tags", free: true, starter: true, pro: true, business: true },
  { label: "Basic scan analytics", free: true, starter: true, pro: true, business: true },
  { label: "CSV import / export", free: true, starter: true, pro: true, business: true },
  { label: "API access", free: false, starter: false, pro: true, business: true },
  { label: "Analytics export", free: false, starter: false, pro: true, business: true },
  { label: "Location heatmap", free: false, starter: false, pro: true, business: true },
  { label: "A/B testing (variants)", free: false, starter: false, pro: true, business: true },
  { label: "White-label branding", free: false, starter: false, pro: false, business: true },
];

export const PRICING_FAQ = [
  {
    q: "What counts as a scan?",
    a: "Each visit to a code's public redirect URL is one scan, counted toward your monthly allowance. Repeated scans by the same person each count.",
  },
  {
    q: "What happens if I exceed my monthly scan limit?",
    a: "Redirects keep working so your audience is never blocked — you'll just be prompted to upgrade for more headroom. We never charge per-scan overage fees.",
  },
  {
    q: "What happens when I reach my code limit?",
    a: "You can't create new codes until you delete some or upgrade. Existing codes keep redirecting normally.",
  },
  {
    q: "Can I change plans at any time?",
    a: "Yes. Upgrades take effect immediately. Downgrades take effect at the end of the current billing period.",
  },
  {
    q: "How does annual billing work?",
    a: "Annual plans are billed once per year at a discount compared with paying monthly. You can switch between monthly and annual at checkout or in the billing portal.",
  },
  {
    q: "What happens to my data if I downgrade or cancel?",
    a: "Your codes and history are retained and dynamic redirects keep working. If you're over the new plan's limits you can't create new codes until you're back under them — nothing is deleted automatically.",
  },
  {
    q: "Do you offer discounts for non-profits or education?",
    a: "Reach out to support to ask about eligibility for special pricing.",
  },
];

/** Format a monthly price, applying the annual discount when requested. */
export function formatPrice(monthly, annual) {
  if (monthly === 0) return "€0";
  const value = annual ? Math.round(monthly * (1 - ANNUAL_DISCOUNT)) : monthly;
  return `€${value}`;
}
